import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../state/AppStateContext";
import { useUi } from "../state/ui";
import { useTheme } from "../state/theme";
import { listAllTerms } from "../rdf/model";
import { buildSearchIndex, searchTerms } from "../rdf/search-index";
import { explorePath, visualizePath } from "../state/navigation";
import type { DatasetDescriptor, TermSummary } from "../rdf/types";
import { KIND_STYLE } from "./kind-style";

const RECENTS_STORAGE_KEY = "oe-recent-terms";
const RECENTS_MAX = 8;

type RecentTerm = Pick<
  TermSummary,
  "iri" | "label" | "prefixedName" | "kind" | "datasetId"
>;

function readRecents(): RecentTerm[] {
  try {
    const raw = localStorage.getItem(RECENTS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as RecentTerm[]) : [];
  } catch {
    return [];
  }
}

export function pushRecentTerm(term: RecentTerm): void {
  try {
    const next = [
      {
        iri: term.iri,
        label: term.label,
        prefixedName: term.prefixedName,
        kind: term.kind,
        datasetId: term.datasetId,
      },
      ...readRecents().filter((r) => r.iri !== term.iri),
    ].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Recents are best-effort.
  }
}

type PaletteItem =
  | { type: "term"; term: TermSummary | RecentTerm }
  | { type: "ontology"; dataset: DatasetDescriptor }
  | { type: "action"; id: string; label: string; hint?: string; run: () => void };

interface Section {
  title: string;
  items: PaletteItem[];
}

export default function CommandPalette() {
  const { paletteOpen, closePalette, openUpload } = useUi();
  const { modelContext, datasets } = useAppState();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // On the Graph page, plain Enter focuses the graph (the modifier flips to
  // docs); everywhere else plain Enter opens docs. Matches user intent per
  // surface without a second search UI.
  const graphMode = location.pathname.startsWith("/visualize");
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const searchIndex = useMemo(
    () => (modelContext ? buildSearchIndex(listAllTerms(modelContext)) : null),
    [modelContext],
  );

  useEffect(() => {
    if (paletteOpen) {
      setQuery("");
      setActiveIndex(0);
      // Focus after the overlay mounts.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [paletteOpen]);

  const sections = useMemo<Section[]>(() => {
    if (!paletteOpen) return [];
    const trimmed = query.trim();
    const lower = trimmed.toLowerCase();

    const actions: PaletteItem[] = [
      {
        type: "action",
        id: "upload",
        label: "Upload ontology (.ttl)…",
        hint: "Add your own Turtle file",
        run: () => openUpload(),
      },
      {
        type: "action",
        id: "explore",
        label: "Go to Documentation",
        hint: "Docs-style browser",
        run: () => navigate("/explore/classes"),
      },
      {
        type: "action",
        id: "visualize",
        label: "Go to Graph",
        hint: "Neighborhood visualization",
        run: () => navigate("/visualize"),
      },
      {
        type: "action",
        id: "theme",
        label: theme === "dark" ? "Switch to light theme" : "Switch to dark theme",
        run: () => toggleTheme(),
      },
    ];

    if (!trimmed) {
      const recents = readRecents();
      const result: Section[] = [];
      if (recents.length > 0) {
        result.push({
          title: "Recent",
          items: recents.map((term) => ({ type: "term", term })),
        });
      }
      result.push({
        title: "Ontologies",
        items: datasets.map((dataset) => ({ type: "ontology", dataset })),
      });
      result.push({ title: "Actions", items: actions });
      return result;
    }

    const result: Section[] = [];
    const terms = searchIndex ? searchTerms(searchIndex, trimmed, 12) : [];
    if (terms.length > 0) {
      result.push({
        title: "Terms",
        items: terms.map((term) => ({ type: "term", term })),
      });
    }
    const matchingDatasets = datasets.filter(
      (d) =>
        d.label.toLowerCase().includes(lower) ||
        d.shortLabel.toLowerCase().includes(lower),
    );
    if (matchingDatasets.length > 0) {
      result.push({
        title: "Ontologies",
        items: matchingDatasets.map((dataset) => ({ type: "ontology", dataset })),
      });
    }
    const matchingActions = actions.filter((a) =>
      a.type === "action" ? a.label.toLowerCase().includes(lower) : false,
    );
    if (matchingActions.length > 0) {
      result.push({ title: "Actions", items: matchingActions });
    }
    return result;
  }, [paletteOpen, query, searchIndex, datasets, navigate, openUpload, theme, toggleTheme]);

  const flatItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const active = listRef.current?.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, sections]);

  if (!paletteOpen) return null;

  const runItem = (item: PaletteItem, modifierHeld: boolean) => {
    closePalette();
    if (item.type === "term") {
      pushRecentTerm(item.term);
      const openInGraph = graphMode !== modifierHeld;
      navigate(
        openInGraph
          ? visualizePath(item.term.iri)
          : explorePath(item.term as TermSummary),
      );
    } else if (item.type === "ontology") {
      navigate(`/explore/ontologies?dataset=${encodeURIComponent(item.dataset.id)}`);
    } else {
      item.run();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closePalette();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = flatItems[activeIndex];
      if (item) runItem(item, event.metaKey || event.ctrlKey);
    }
  };

  let runningIndex = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[12vh] backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closePalette();
      }}
    >
      <div
        className="oe-pop-in w-full max-w-xl overflow-hidden rounded-xl border border-[var(--oe-border)] bg-[var(--oe-surface)]"
        style={{ boxShadow: "var(--oe-shadow-lg)" }}
        role="dialog"
        aria-modal="true"
        aria-label="Search ontologies"
      >
        <div className="flex items-center gap-2.5 border-b border-[var(--oe-border)] px-4">
          <SearchIcon />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search classes, properties, ontologies…"
            className="w-full bg-transparent py-3.5 text-[15px] text-[var(--oe-text)] placeholder:text-[var(--oe-text-muted)] focus:outline-none"
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="shrink-0 rounded border border-[var(--oe-border)] bg-[var(--oe-surface-raised)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--oe-text-muted)]">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[52vh] overflow-y-auto overscroll-contain p-2">
          {flatItems.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-[var(--oe-text-muted)]">
              No results for “{query}”.
            </p>
          ) : (
            sections.map((section) => (
              <div key={section.title} className="mb-1">
                <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--oe-text-muted)]">
                  {section.title}
                </p>
                {section.items.map((item) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  const isActive = index === activeIndex;
                  return (
                    <PaletteRow
                      key={itemKey(item)}
                      item={item}
                      datasets={datasets}
                      isActive={isActive}
                      onHover={() => setActiveIndex(index)}
                      onRun={(openInGraph) => runItem(item, openInGraph)}
                    />
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-[var(--oe-border)] bg-[var(--oe-surface-raised)]/60 px-4 py-2 text-[11px] text-[var(--oe-text-muted)]">
          <span>
            <kbd className="font-sans">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="font-sans">↵</kbd> {graphMode ? "focus in graph" : "open docs"}
          </span>
          <span>
            <kbd className="font-sans">⌘↵</kbd> {graphMode ? "open docs" : "open in graph"}
          </span>
        </div>
      </div>
    </div>
  );
}

function itemKey(item: PaletteItem): string {
  if (item.type === "term") return `term:${item.term.iri}`;
  if (item.type === "ontology") return `ontology:${item.dataset.id}`;
  return `action:${item.id}`;
}

function PaletteRow({
  item,
  datasets,
  isActive,
  onHover,
  onRun,
}: {
  item: PaletteItem;
  datasets: DatasetDescriptor[];
  isActive: boolean;
  onHover: () => void;
  onRun: (modifierHeld: boolean) => void;
}) {
  const base = `flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm ${
    isActive ? "bg-[var(--oe-accent-soft)]" : ""
  }`;

  if (item.type === "term") {
    const style = KIND_STYLE[item.term.kind];
    const dataset = datasets.find((d) => d.id === item.term.datasetId);
    return (
      <button
        type="button"
        className={base}
        data-active={isActive}
        onMouseMove={onHover}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => onRun(event.metaKey || event.ctrlKey)}
      >
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold"
          style={{ color: style.color, backgroundColor: `${style.color}1a` }}
          aria-hidden="true"
        >
          {style.glyph}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-[var(--oe-text)]">
            {item.term.label ?? item.term.prefixedName}
          </span>
          <span className="block truncate font-mono text-[11px] text-[var(--oe-text-muted)]">
            {item.term.prefixedName}
          </span>
        </span>
        {dataset && (
          <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-[var(--oe-text-muted)]">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: dataset.accentColor }}
            />
            {dataset.shortLabel}
          </span>
        )}
      </button>
    );
  }

  if (item.type === "ontology") {
    return (
      <button
        type="button"
        className={base}
        data-active={isActive}
        onMouseMove={onHover}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => onRun(false)}
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: item.dataset.accentColor }}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate font-medium text-[var(--oe-text)]">
          {item.dataset.label}
        </span>
        {item.dataset.origin === "user" && (
          <span className="shrink-0 rounded-full border border-[var(--oe-border)] px-1.5 py-0.5 text-[10px] text-[var(--oe-text-muted)]">
            uploaded
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={base}
      data-active={isActive}
      onMouseMove={onHover}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onRun(false)}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[var(--oe-text-muted)]" aria-hidden="true">
        <BoltIcon />
      </span>
      <span className="flex-1 font-medium text-[var(--oe-text)]">{item.label}</span>
      {item.hint && (
        <span className="shrink-0 text-[11px] text-[var(--oe-text-muted)]">{item.hint}</span>
      )}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-[var(--oe-text-muted)]" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}
